import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';

import Cerrar from '../../img/Cerrar.png'
import './Fotos.css';

const SECCIONES = [
    { id: "1", nombre: "Fachada" },
    { id: "2", nombre: "Andenes de carga y descarga" },
    { id: "3", nombre: "Rampa de carga y descarga" },
    { id: "4", nombre: "Interior" },
    { id: "5", nombre: "Fullfilment (Pick & Pack)" },
    { id: "6", nombre: "Servicio de valor agregado (maquila)" },
    { id: "7", nombre: "Área de oficinas" },
    { id: "8", nombre: "Recepción" },
    { id: "9", nombre: "Vídeo" },
];

const seccionVisible = (seccion, publicacion) => {
    if (seccion.id === "5") return publicacion?.operativeServices?.includes("7");
    if (seccion.id === "6") return publicacion?.operativeServices?.includes("12");
    return true;
};

const Lightbox = ({ items, index, onClose, onChange }) => {
    const filmRef = useRef(null);
    const item = items[index];
    const total = items.length;

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onChange((index - 1 + total) % total);
            if (e.key === "ArrowRight") onChange((index + 1) % total);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [index, total, onClose, onChange]);

    useEffect(() => {
        const el = filmRef.current?.querySelector(".is-active");
        el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }, [index]);

    if (!item) return null;

    return (
        <div className="gal-lb" role="dialog" aria-modal="true" aria-label="Visor de fotos">
            <div className="gal-lb__top">
                <button type="button" className="gal-lb__close" onClick={onClose}>
                    Cerrar
                </button>
                <div className="gal-lb__meta">
                    <span className="gal-lb__cat">{item.nombre}</span>
                    <span className="gal-lb__count">{index + 1} / {total}</span>
                </div>
                <div className="gal-lb__spacer" />
            </div>

            <div className="gal-lb__stage">
                <button
                    type="button"
                    className="gal-lb__nav gal-lb__nav--prev"
                    aria-label="Anterior"
                    onClick={() => onChange((index - 1 + total) % total)}
                >
                    ‹
                </button>

                <div className="gal-lb__media">
                    {item.esVideo ? (
                        <video
                            key={item.url}
                            className="gal-lb__asset"
                            src={item.url + "#t=0.01"}
                            controls
                            autoPlay
                            muted
                        />
                    ) : (
                        <img className="gal-lb__asset" src={item.url} alt={item.nombre} />
                    )}
                </div>

                <button
                    type="button"
                    className="gal-lb__nav gal-lb__nav--next"
                    aria-label="Siguiente"
                    onClick={() => onChange((index + 1) % total)}
                >
                    ›
                </button>
            </div>

            <div className="gal-lb__film" ref={filmRef}>
                {items.map((foto, i) => (
                    <button
                        type="button"
                        key={`${foto.url}-${i}`}
                        className={"gal-lb__thumb" + (i === index ? " is-active" : "")}
                        onClick={() => onChange(i)}
                        aria-label={`${foto.nombre} ${i + 1}`}
                    >
                        {foto.esVideo ? (
                            <video src={foto.url + "#t=0.5"} muted playsInline />
                        ) : (
                            <img src={foto.url} alt="" />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

const Fotos = () => {
    const { id, idPhoto } = useParams();
    const { publicacion } = useOutletContext();
    const navigate = useNavigate();

    const [seccionActiva, setSeccionActiva] = useState(null);
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [navAbierta, setNavAbierta] = useState(false);

    const { porSeccion, items, seccionesDisponibles } = useMemo(() => {
        const agrupadas = {};
        (publicacion?.Photos || []).forEach((foto) => {
            if (!agrupadas[foto.idCategoryPhoto]) agrupadas[foto.idCategoryPhoto] = [];
            agrupadas[foto.idCategoryPhoto].push(foto);
        });

        const disponibles = SECCIONES.filter(
            (s) => seccionVisible(s, publicacion) && agrupadas[s.id]?.length
        );

        const flat = [];
        disponibles.forEach((seccion) => {
            agrupadas[seccion.id].forEach((foto) => {
                flat.push({
                    ...foto,
                    nombre: seccion.nombre,
                    seccionId: seccion.id,
                    esVideo: seccion.id === "9",
                    index: flat.length,
                });
            });
        });

        return {
            porSeccion: agrupadas,
            items: flat,
            seccionesDisponibles: disponibles,
        };
    }, [publicacion]);

    useEffect(() => {
        if (!seccionesDisponibles.length) return;
        if (!seccionActiva || !seccionesDisponibles.some((s) => s.id === seccionActiva)) {
            setSeccionActiva(seccionesDisponibles[0].id);
        }
    }, [seccionesDisponibles, seccionActiva]);

    useEffect(() => {
        if (!items.length || idPhoto == null) return;

        if (idPhoto === "video") {
            const video = items.find((x) => x.esVideo);
            if (video) {
                setSeccionActiva(video.seccionId);
                setLightboxIndex(video.index);
            }
            return;
        }

        const n = Number(idPhoto);
        if (!Number.isNaN(n) && items[n]) {
            setSeccionActiva(items[n].seccionId);
            setLightboxIndex(n);
        }
    }, [idPhoto, items]);

    const fotosSeccion = porSeccion[seccionActiva] || [];
    const seccionActual = seccionesDisponibles.find((s) => s.id === seccionActiva);
    const totalFotos = items.length;
    const lightboxAbierto = lightboxIndex !== null && items[lightboxIndex];

    const abrirFoto = (fotoGlobal) => {
        setLightboxIndex(fotoGlobal.index);
    };

    const abrirDesdeSeccion = (fotoLocal, idxLocal) => {
        const global = items.find(
            (x) => x.seccionId === seccionActiva && x.url === fotoLocal.url
        ) || items.filter((x) => x.seccionId === seccionActiva)[idxLocal];
        if (global) abrirFoto(global);
    };

    const cerrarGaleria = () => {
        navigate("/almacen/" + id, { replace: true });
    };

    return (
        <div className="gal-shell" role="dialog" aria-modal="true" aria-label="Galería del almacén">
            <header className="gal-top">
                <button type="button" className="gal-top__close" onClick={cerrarGaleria}>
                    <img src={Cerrar} alt="" />
                    <span>Salir</span>
                </button>
                <div className="gal-top__center">
                    <h1>Galería</h1>
                    <p>{totalFotos} {totalFotos === 1 ? "archivo" : "archivos"} · {seccionesDisponibles.length} zonas</p>
                </div>
                <button
                    type="button"
                    className="gal-top__menu"
                    onClick={() => setNavAbierta((v) => !v)}
                >
                    Zonas
                </button>
            </header>

            <div className={"gal-body" + (navAbierta ? " is-nav-open" : "")}>
                <aside className="gal-nav">
                    <div className="gal-nav__head">
                        <span>Explorar zonas</span>
                        <button type="button" className="gal-nav__dismiss" onClick={() => setNavAbierta(false)}>
                            Listo
                        </button>
                    </div>
                    <nav className="gal-nav__list" aria-label="Secciones de la galería">
                        {seccionesDisponibles.map((seccion) => {
                            const count = porSeccion[seccion.id]?.length || 0;
                            const activa = seccion.id === seccionActiva;
                            const preview = porSeccion[seccion.id]?.[0];
                            return (
                                <button
                                    type="button"
                                    key={seccion.id}
                                    className={"gal-nav__item" + (activa ? " is-active" : "")}
                                    onClick={() => {
                                        setSeccionActiva(seccion.id);
                                        setNavAbierta(false);
                                    }}
                                >
                                    <div className="gal-nav__thumb">
                                        {preview && (
                                            seccion.id === "9"
                                                ? <video src={preview.url + "#t=0.5"} muted playsInline />
                                                : <img src={preview.url} alt="" />
                                        )}
                                    </div>
                                    <div className="gal-nav__text">
                                        <strong>{seccion.nombre}</strong>
                                        <span>{count} {count === 1 ? "foto" : seccion.id === "9" ? "vídeo" : "fotos"}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </nav>
                </aside>

                {navAbierta && <button type="button" className="gal-scrim" aria-label="Cerrar menú" onClick={() => setNavAbierta(false)} />}

                <main className="gal-stage">
                    <div className="gal-stage__head">
                        <div>
                            <p className="gal-stage__eyebrow">Zona seleccionada</p>
                            <h2>{seccionActual?.nombre || "Sin fotos"}</h2>
                        </div>
                        <span className="gal-stage__badge">
                            {fotosSeccion.length} {seccionActiva === "9" ? (fotosSeccion.length === 1 ? "vídeo" : "vídeos") : (fotosSeccion.length === 1 ? "foto" : "fotos")}
                        </span>
                    </div>

                    {!fotosSeccion.length ? (
                        <div className="gal-empty">No hay imágenes en esta sección.</div>
                    ) : (
                        <div className={"gal-grid" + (fotosSeccion.length === 1 ? " gal-grid--solo" : "")}>
                            {fotosSeccion.map((foto, i) => {
                                const esHero = i === 0 && fotosSeccion.length !== 2;
                                const esVideo = seccionActiva === "9";
                                return (
                                    <button
                                        type="button"
                                        key={`${foto.url}-${i}`}
                                        className={
                                            "gal-card"
                                            + (esHero ? " gal-card--hero" : "")
                                            + (fotosSeccion.length === 2 ? " gal-card--half" : "")
                                        }
                                        onClick={() => abrirDesdeSeccion(foto, i)}
                                    >
                                        {esVideo ? (
                                            <video src={foto.url + "#t=1.0"} muted playsInline />
                                        ) : (
                                            <img src={foto.url} alt={seccionActual?.nombre || "Foto"} />
                                        )}
                                        <span className="gal-card__hint">{esVideo ? "Ver vídeo" : "Ampliar"}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </main>
            </div>

            {lightboxAbierto && (
                <Lightbox
                    items={items}
                    index={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                    onChange={setLightboxIndex}
                />
            )}
        </div>
    );
};

export default Fotos;
